import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TIP_CHECKOUT_FEEDBACK_MAX_LENGTH,
  TIP_CHECKOUT_STAR_VALUES,
  getTipCheckoutRatingFillLevels,
} from '../model';

import { ActiveRatingStarIcon } from './tip-checkout-rating-card.graphics';
import {
  FeedbackBlock,
  FeedbackCounter,
  FeedbackMetaRow,
  FeedbackPrompt,
  FeedbackStar,
  FeedbackTextArea,
  FilledFeedbackStar,
  RatingCard,
  StarButton,
  StarsRow,
  Title,
} from './tip-checkout-rating-card.styles';

type TipCheckoutRatingCardProps = {
  feedbackCharacterCount: number;
  feedbackPromptKey: string | null;
  feedbackText: string;
  feedbackTitle: string;
  isFeedbackPrefilled: boolean;
  selectedRating: number | null;
  shouldShowFeedbackField: boolean;
  onFeedbackChange: (nextValue: string) => void;
  onFeedbackFocus: () => void;
  onRatingSelect: (ratingValue: number) => void;
};

export function TipCheckoutRatingCard({
  feedbackCharacterCount,
  feedbackPromptKey,
  feedbackText,
  feedbackTitle,
  isFeedbackPrefilled,
  selectedRating,
  shouldShowFeedbackField,
  onFeedbackChange,
  onFeedbackFocus,
  onRatingSelect,
}: TipCheckoutRatingCardProps) {
  const { t } = useTranslation();
  const [activeAnimationVersion, setActiveAnimationVersion] = useState(0);
  const starFillLevels =
    selectedRating === null
      ? []
      : getTipCheckoutRatingFillLevels(selectedRating);

  const handleRatingSelect = (ratingValue: number) => {
    setActiveAnimationVersion(currentVersion => currentVersion + 1);
    onRatingSelect(ratingValue);
  };

  return (
    <RatingCard>
      <Title>{feedbackTitle}</Title>

      <StarsRow>
        {TIP_CHECKOUT_STAR_VALUES.map((starValue, index) => {
          const isActive = selectedRating === starValue;
          const isFilled = starFillLevels[index] === 1;

          return (
            <StarButton
              $isFilled={isFilled}
              aria-label={t('tipsCheckout.ratingAria', { value: starValue })}
              aria-pressed={isActive}
              key={starValue}
              type="button"
              onClick={() => handleRatingSelect(starValue)}
            >
              {isActive ? (
                <ActiveRatingStarIcon
                  key={`${starValue}-${activeAnimationVersion}`}
                />
              ) : isFilled ? (
                <FilledFeedbackStar />
              ) : (
                <FeedbackStar />
              )}
            </StarButton>
          );
        })}
      </StarsRow>

      {shouldShowFeedbackField && feedbackPromptKey && (
        <FeedbackBlock>
          <FeedbackMetaRow>
            <FeedbackPrompt>{t(feedbackPromptKey)}</FeedbackPrompt>
            <FeedbackCounter>
              {t('tipsCheckout.feedbackCounter', {
                count: feedbackCharacterCount,
                max: TIP_CHECKOUT_FEEDBACK_MAX_LENGTH,
              })}
            </FeedbackCounter>
          </FeedbackMetaRow>

          <FeedbackTextArea
            $isPrefilled={isFeedbackPrefilled}
            aria-label={t('tipsCheckout.feedbackAria')}
            maxLength={TIP_CHECKOUT_FEEDBACK_MAX_LENGTH}
            placeholder={t('tipsCheckout.feedbackPlaceholder')}
            rows={2}
            value={feedbackText}
            onChange={event => onFeedbackChange(event.target.value)}
            onFocus={onFeedbackFocus}
          />
        </FeedbackBlock>
      )}
    </RatingCard>
  );
}
